/**
 * ReturnToMainAgent — the always-on affordance that takes a user from any
 * satellite app (care-planner / compatibility / alg) back to the main digital-twin agent
 * at the tenant root host.
 *
 * Joël's requirement: a user must ALWAYS be able to reach the twin from a
 * satellite app — not only on child sessions spawned by a parent. So it lives in
 * every satellite header (Home, Session, Run, Studio), to the LEFT of any
 * in-app back-chevron, which is a DISTINCT affordance (in-app back vs
 * leave-to-twin).
 *
 * Lives in `frontend/src/components/` (with the other cross-app shared
 * components like ShareDialog that the satellite apps already consume via
 * `@frontend/...`) so npm + `@frontend` + type resolution work — a shared dir
 * outside `frontend/` has no node_modules in its module-resolution walk, which
 * is why the only file that ever lived in one (`vite-app-preset.ts`) had to
 * import nothing but `node:path`. The host derivation lives
 * in the sibling `../api/mainAppUrl`.
 *
 * Design (DESIGN.md):
 *  - Icon-only `House` glyph in the back-chevron grammar (NOT the +New pill):
 *    a left-edge utility glyph, not a primary CTA.
 *  - Single Action-Blue accent expressed via the focus ring.
 *  - 44px touch target (bedside + iOS-Safari), `active:scale-95` system press.
 *  - Label is the deployment brand name via `useBrandName()`, carried on
 *    `aria-label` + `title` only (no visible copy; non-paternalistic).
 *
 * Navigation crosses subdomains (cross-origin), so it is a hard
 * `window.location.assign` to the derived twin URL, mirroring the login
 * redirect — react-router cannot route across origins.
 */

import { House } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { mainAppUrl } from '@/api/mainAppUrl';
import { useBrandName } from '@/i18n/useBrandName';
import { cn } from '@/lib/utils';

export interface ReturnToMainAgentProps {
  /** Extra classes for per-header alignment (e.g. negative margins). */
  className?: string;
}

export function ReturnToMainAgent({ className }: ReturnToMainAgentProps) {
  const { t } = useTranslation();
  const brand = useBrandName();
  // The label IS the brand name (the twin's identity). Keyed so a future
  // wording change is i18n-driven; the brand is interpolated, not hardcoded.
  // Fall back to the i18n app name if the deployment ships an empty
  // `branding.name` (useBrandName returns '' for an empty-string config) — an
  // icon-only button must never have an empty accessible name (WCAG 1.1.1).
  const label = t('common.returnToMainAgent', '{{brand}}', { brand }) || t('app.name');

  return (
    <button
      type="button"
      onClick={() => window.location.assign(mainAppUrl())}
      aria-label={label}
      title={label}
      data-testid="return-to-main-agent"
      className={cn(
        'flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[var(--color-surface-mute,#6B7280)] transition',
        'active:scale-95 hover:bg-[var(--color-surface-canvas,#F3F4F6)] hover:text-[var(--color-surface-ink,#1F2937)]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)]',
        className,
      )}
    >
      <House className="h-5 w-5" aria-hidden="true" />
    </button>
  );
}
